import { Margin, usePDF } from "react-to-pdf";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import PrintIcon from "@mui/icons-material/Print";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { axiosInstance } from "../../../../utils/networking";

const PRIMARY = "#9D2362";
const CARD_BORDER = "1px solid #ececf0";

const statusLabels: { [key: string]: string } = {
  open: "Open (Unassigned)",
  assigned: "Assigned to Case",
};

const statusColors: { [key: string]: string } = {
  open: "#fdb814",
  assigned: "#2e7d32",
};

// Distinct colors for the intake source segments (general / global /
// hospitality / other), keyed by the normalized source value.
const sourceColors: { [key: string]: string } = {
  general: PRIMARY,
  global: "#3F51B5",
  hospitality: "#00897b",
  other: "#9e9e9e",
};

// ---- Small presentational helpers -------------------------------------------

const SectionCard = ({ children, sx }: { children: any; sx?: any }) => (
  <Paper
    elevation={0}
    sx={{
      p: 3,
      borderRadius: "16px",
      border: CARD_BORDER,
      backgroundColor: "white",
      ...sx,
    }}
  >
    {children}
  </Paper>
);

const SectionTitle = ({
  children,
  subtitle,
}: {
  children: any;
  subtitle?: string;
}) => (
  <Box sx={{ mb: 2 }}>
    <Typography
      variant="h6"
      sx={{
        fontWeight: 700,
        color: "#2c2c34",
        borderLeft: `4px solid ${PRIMARY}`,
        pl: 1.5,
        lineHeight: 1.25,
      }}
    >
      {children}
    </Typography>
    {subtitle ? (
      <Typography variant="body2" sx={{ color: "#8a8a93", pl: 1.5, mt: 0.5 }}>
        {subtitle}
      </Typography>
    ) : null}
  </Box>
);

const StatusChip = ({ status }: { status: string }) => {
  const color = statusColors[status] || "#9e9e9e";
  return (
    <Chip
      label={statusLabels[status] || status}
      size="small"
      sx={{
        backgroundColor: `${color}1f`,
        color: color,
        fontWeight: 600,
        borderRadius: "6px",
      }}
    />
  );
};

const AssignedBar = ({ rate }: { rate: number }) => {
  const pct = Math.round((rate || 0) * 100);
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        justifyContent: "flex-end",
      }}
    >
      <Box sx={{ width: 64 }}>
        <LinearProgress
          variant="determinate"
          value={pct}
          sx={{
            height: 7,
            borderRadius: 4,
            backgroundColor: "#eef0f2",
            "& .MuiLinearProgress-bar": {
              backgroundColor: "#2e7d32",
              borderRadius: 4,
            },
          }}
        />
      </Box>
      <Typography variant="body2" sx={{ minWidth: 36, textAlign: "right" }}>
        {pct}%
      </Typography>
    </Box>
  );
};

const StatCard = ({
  label,
  value,
  accent,
}: {
  label: string;
  value: any;
  accent: string;
}) => (
  <Paper
    elevation={0}
    sx={{
      flex: "1 1 150px",
      p: 2.5,
      borderRadius: "16px",
      border: CARD_BORDER,
      borderTop: `3px solid ${accent}`,
      backgroundColor: "white",
      textAlign: "center",
    }}
  >
    <Typography
      sx={{
        fontSize: "2rem",
        fontWeight: 800,
        color: "#2c2c34",
        lineHeight: 1.1,
      }}
    >
      {value}
    </Typography>
    <Typography
      variant="caption"
      sx={{
        color: "#8a8a93",
        textTransform: "uppercase",
        letterSpacing: "0.5px",
        fontWeight: 600,
      }}
    >
      {label}
    </Typography>
  </Paper>
);

const ChartCard = ({
  title,
  subtitle,
  height,
  children,
}: {
  title: string;
  subtitle?: string;
  height: number | string;
  children: any;
}) => (
  <SectionCard sx={{ flex: "1 1 380px", minWidth: 0 }}>
    <SectionTitle subtitle={subtitle}>{title}</SectionTitle>
    <Box sx={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        {children}
      </ResponsiveContainer>
    </Box>
  </SectionCard>
);

// Lifecycle order so the Status column sorts by progress
// (open → assigned) rather than alphabetically.
const statusOrder: { [key: string]: number } = {
  open: 1,
  assigned: 2,
};

// Sortable detail table listing the submissions within one source group.
const SubmissionDetailTable = ({ rows }: { rows: any[] }) => {
  const [sortKey, setSortKey] = useState("entry_date");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  const columns = [
    { key: "company_name", label: "Business" },
    { key: "contact_name", label: "Contact" },
    { key: "city", label: "City" },
    { key: "entry_date", label: "Submitted" },
    { key: "status", label: "Status" },
    { key: "consulted", label: "Consulted" },
  ];

  const getSortValue = (row: any, key: string) => {
    if (key === "entry_date")
      return row.entry_date ? new Date(row.entry_date).getTime() : 0;
    if (key === "status")
      return statusOrder[row.status] !== undefined
        ? statusOrder[row.status]
        : -1;
    if (key === "consulted") return row.consulted ? 1 : 0;
    return (row[key] || "").toString().toLowerCase();
  };

  const sortedRows = [...rows].sort((a, b) => {
    const av = getSortValue(a, sortKey);
    const bv = getSortValue(b, sortKey);
    if (av < bv) return sortDir === "asc" ? -1 : 1;
    if (av > bv) return sortDir === "asc" ? 1 : -1;
    return 0;
  });

  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          {columns.map((col) => (
            <TableCell
              key={col.key}
              sortDirection={sortKey === col.key ? sortDir : false}
            >
              <TableSortLabel
                active={sortKey === col.key}
                direction={sortKey === col.key ? sortDir : "asc"}
                onClick={() => handleSort(col.key)}
              >
                {col.label}
              </TableSortLabel>
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {sortedRows.map((row: any) => (
          <TableRow key={row.id}>
            <TableCell>
              <Typography sx={{ fontWeight: 600 }}>
                {row.company_name}
              </Typography>
              {row.industry ? (
                <Typography variant="caption" sx={{ color: "#8a8a93" }}>
                  {row.industry}
                </Typography>
              ) : null}
            </TableCell>
            <TableCell>{row.contact_name}</TableCell>
            <TableCell>{row.city}</TableCell>
            <TableCell>
              {row.entry_date ? dayjs(row.entry_date).format("YYYY-MM-DD") : ""}
            </TableCell>
            <TableCell>
              <StatusChip status={row.status} />
            </TableCell>
            <TableCell>
              {row.consulted ? (
                <Chip
                  label="Yes"
                  size="small"
                  sx={{
                    backgroundColor: `${PRIMARY}1f`,
                    color: PRIMARY,
                    fontWeight: 600,
                    borderRadius: "6px",
                  }}
                />
              ) : (
                <Typography variant="body2" sx={{ color: "#bdbdbd" }}>
                  —
                </Typography>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

// One source row in the "By Source" table that expands to show its
// submissions.
const SourceRow = ({
  source,
  submissionRows,
}: {
  source: any;
  submissionRows: any[];
}) => {
  const [open, setOpen] = useState(false);
  const rate = source.total > 0 ? source.assigned / source.total : 0;
  return (
    <>
      <TableRow
        hover
        onClick={() => setOpen(!open)}
        sx={{ "& > *": { borderBottom: "unset" }, cursor: "pointer" }}
      >
        <TableCell sx={{ width: 40 }}>
          <IconButton size="small">
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ fontWeight: 600 }}>{source.label}</TableCell>
        <TableCell align="right">{source.total}</TableCell>
        <TableCell align="right">{source.open}</TableCell>
        <TableCell align="right">{source.assigned}</TableCell>
        <TableCell align="right">{source.consultations}</TableCell>
        <TableCell align="right" sx={{ width: 160 }}>
          <AssignedBar rate={rate} />
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell sx={{ paddingY: 0, border: 0 }} colSpan={7}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box
              sx={{
                m: 1,
                p: 2,
                backgroundColor: "#faf7f9",
                borderRadius: "12px",
                border: "1px solid #f0e6ec",
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{ mb: 1, color: PRIMARY, fontWeight: 700 }}
              >
                Submissions ({submissionRows.length})
              </Typography>
              <SubmissionDetailTable rows={submissionRows} />
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const fmtDate = (date: Dayjs | null) => (date ? date.format("YYYY-MM-DD") : "");

interface WebIntakeReportProps {
  // When provided, the report follows the Reporting hub's shared (URL-backed)
  // date filter instead of its own pickers, which are then hidden.
  controlledRange?: { start: Dayjs | null; end: Dayjs | null };
}

const WebIntakeReport = ({ controlledRange }: WebIntakeReportProps) => {
  const [report, setReport] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);

  const controlled = controlledRange !== undefined;
  const effectiveStart = controlled ? controlledRange.start : startDate;
  const effectiveEnd = controlled ? controlledRange.end : endDate;

  const fetchReport = (start: Dayjs | null, end: Dayjs | null) => {
    setLoading(true);
    let url = `/reports/webintake`;
    const params: string[] = [];
    if (start) params.push(`startDate=${start.format("YYYY-MM-DD")}`);
    if (end) params.push(`endDate=${end.format("YYYY-MM-DD")}`);
    if (params.length > 0) url = url + `?` + params.join("&");
    axiosInstance
      .get(url)
      .then((response: any) => {
        setReport(response.data);
      })
      .finally(() => setLoading(false));
  };

  // Uncontrolled: one fetch on mount (the Apply button drives the rest).
  // Controlled: refetch whenever the hub's range changes — compared as
  // formatted strings so a new Dayjs instance alone never refetches.
  const controlledStartKey = controlled ? fmtDate(controlledRange.start) : "";
  const controlledEndKey = controlled ? fmtDate(controlledRange.end) : "";
  useEffect(() => {
    if (controlled) fetchReport(controlledRange.start, controlledRange.end);
    else fetchReport(null, null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controlled, controlledStartKey, controlledEndKey]);

  const { toPDF, targetRef } = usePDF({
    filename: "Web_Intake_Report.pdf",
    page: { margin: Margin.SMALL, format: "letter" },
    overrides: {
      canvas: {
        removeContainer: false,
      },
    },
  });

  const handleCsvDownload = () => {
    if (!report) return;
    let csvContent: any[] = [];
    let csvHeading = [
      "Business",
      "Contact",
      "Email",
      "Phone",
      "City",
      "State",
      "Zip Code",
      "Source",
      "Status",
      "Consulted",
      "Submitted",
      "How They Heard",
    ];
    csvContent.push(csvHeading);

    for (const row of report.rows) {
      let csvAnswer = [];
      csvAnswer.push(row.company_name);
      csvAnswer.push(row.contact_name);
      csvAnswer.push(row.email);
      csvAnswer.push(row.phone);
      csvAnswer.push(row.city);
      csvAnswer.push(row.state);
      csvAnswer.push(row.zip);
      csvAnswer.push(sourceLabelOf(row.source));
      csvAnswer.push(statusLabels[row.status] || row.status);
      csvAnswer.push(row.consulted ? "Yes" : "No");
      csvAnswer.push(
        row.entry_date ? dayjs(row.entry_date).format("YYYY-MM-DD") : "",
      );
      csvAnswer.push(row.heard_about);
      csvContent.push(csvAnswer);
    }

    let csvString = ``;
    for (const row of csvContent) {
      let rowString = `\n`;
      for (const item of row) {
        rowString = rowString + `"${item}",`;
      }
      csvString = csvString + rowString;
    }

    const blob = new Blob([csvString], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `web_intake.csv`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);

    axiosInstance.post("/export_log", {
      export_type: "web_intake_report",
      record_count: report.rows.length,
      filters: {
        report: "WebIntake",
        startDate: fmtDate(effectiveStart) || null,
        endDate: fmtDate(effectiveEnd) || null,
      },
    });
  };

  // Resolve a source label from a row's normalized source using the
  // report's bySource list (falls back to the raw value).
  const sourceLabelOf = (source: string) => {
    if (!report) return source;
    const match = report.bySource.find((entry: any) => entry.source === source);
    return match ? match.label : source;
  };

  const rangeLabel =
    effectiveStart || effectiveEnd
      ? `${
          effectiveStart ? effectiveStart.format("MMM D, YYYY") : "Beginning"
        } – ${effectiveEnd ? effectiveEnd.format("MMM D, YYYY") : "Today"}`
      : "All time";

  const toolbar = (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        mb: 3,
        borderRadius: "16px",
        border: CARD_BORDER,
        display: "flex",
        flexWrap: "wrap",
        gap: 2,
        alignItems: "center",
        justifyContent: controlled ? "flex-end" : "space-between",
      }}
    >
      {/* Embedded in the Reporting hub the shared filter bar owns the range,
          so the report's own date controls are hidden. */}
      {controlled ? null : (
        <Box
          sx={{
            display: "flex",
            gap: 1.5,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Start Date"
              value={startDate}
              onChange={(date) => setStartDate(date)}
              disableFuture={true}
              slotProps={{ textField: { size: "small" } }}
            />
            <DatePicker
              label="End Date"
              value={endDate}
              onChange={(date) => setEndDate(date)}
              shouldDisableDate={(date: Dayjs) =>
                startDate !== null && date < startDate
              }
              slotProps={{ textField: { size: "small" } }}
            />
          </LocalizationProvider>
          <Button
            variant="contained"
            onClick={() => fetchReport(startDate, endDate)}
            sx={{
              backgroundColor: PRIMARY,
              "&:hover": { backgroundColor: "#7A1B4D" },
            }}
          >
            Apply
          </Button>
          <Button
            variant="text"
            onClick={() => {
              setStartDate(null);
              setEndDate(null);
              fetchReport(null, null);
            }}
          >
            Reset
          </Button>
        </Box>
      )}
      <Box sx={{ display: "flex", gap: 1.5 }}>
        <Button
          variant="outlined"
          startIcon={<PrintIcon />}
          onClick={() => {
            toPDF();
            axiosInstance.post("/export_log", {
              export_type: "report_pdf",
              record_count: report ? report.rows.length : 0,
              filters: {
                report: "WebIntake",
                startDate: fmtDate(effectiveStart) || null,
                endDate: fmtDate(effectiveEnd) || null,
              },
            });
          }}
        >
          Print
        </Button>
        <Button
          variant="outlined"
          startIcon={<FileDownloadIcon />}
          onClick={handleCsvDownload}
        >
          CSV
        </Button>
      </Box>
    </Paper>
  );

  const pageSx = {
    backgroundColor: "#f6f7f9",
    p: { xs: 2, md: 3 },
    minHeight: "100%",
  };

  if (loading && !report) {
    return (
      <Box
        sx={{
          ...pageSx,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 400,
        }}
      >
        <CircularProgress sx={{ color: PRIMARY }} />
      </Box>
    );
  }

  if (!report) {
    return <Box sx={pageSx}>{toolbar}</Box>;
  }

  const sourceData = report.bySource.map((entry: any) => ({
    key: entry.source,
    name: entry.label,
    value: entry.total,
  }));

  const monthlyMap: { [key: string]: any } = {};
  report.rows.forEach((row: any) => {
    if (!row.entry_date) return;
    const month = dayjs(row.entry_date).format("YYYY-MM");
    if (!monthlyMap[month]) {
      monthlyMap[month] = { month: month, Assigned: 0, Open: 0 };
    }
    if (row.status === "assigned") {
      monthlyMap[month].Assigned++;
    } else {
      monthlyMap[month].Open++;
    }
  });
  const monthlyData = Object.values(monthlyMap).sort((a: any, b: any) =>
    a.month.localeCompare(b.month),
  );

  const cityChartData = report.byCity.slice(0, 10).map((entry: any) => ({
    name: entry.city || "Not Specified",
    Submissions: entry.total,
  }));

  const ownershipData = [
    { name: "Women Owned", Businesses: report.summary.women_owned },
    { name: "Veteran Owned", Businesses: report.summary.veteran_owned },
    { name: "Minority Owned", Businesses: report.summary.minority_owned },
  ];

  const rowsBySource: { [key: string]: any[] } = {};
  report.rows.forEach((row: any) => {
    if (!rowsBySource[row.source]) rowsBySource[row.source] = [];
    rowsBySource[row.source].push(row);
  });

  const statCards = [
    {
      label: "Total Submissions",
      value: report.summary.total,
      accent: PRIMARY,
    },
    {
      label: "Open (Unassigned)",
      value: report.summary.open,
      accent: "#fdb814",
    },
    { label: "Assigned", value: report.summary.assigned, accent: "#2e7d32" },
    {
      label: "Consulted",
      value: report.summary.consultations,
      accent: PRIMARY,
    },
    {
      label: "Consultation Rate",
      value: `${Math.round(report.summary.consultation_rate * 100)}%`,
      accent: "#00897b",
    },
    {
      label: "Minority Owned",
      value: report.summary.minority_owned,
      accent: "#3F51B5",
    },
    {
      label: "Women Owned",
      value: report.summary.women_owned,
      accent: "#00897b",
    },
  ];

  const isEmpty = report.rows.length === 0;

  return (
    <Box sx={pageSx}>
      {toolbar}

      <Box ref={targetRef}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 800, color: "#2c2c34" }}>
            Web Intake Report
          </Typography>
          <Typography variant="body2" sx={{ color: "#8a8a93", mt: 0.5 }}>
            {rangeLabel}
          </Typography>
        </Box>

        {isEmpty ? (
          <SectionCard sx={{ textAlign: "center", py: 8 }}>
            <Typography variant="h6" sx={{ color: "#8a8a93" }}>
              No web intake submissions found for this period
            </Typography>
            <Typography variant="body2" sx={{ color: "#aaa", mt: 1 }}>
              Try widening the date range or resetting the filter.
            </Typography>
          </SectionCard>
        ) : (
          <Stack spacing={3}>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              {statCards.map((card) => (
                <StatCard
                  key={card.label}
                  label={card.label}
                  value={card.value}
                  accent={card.accent}
                />
              ))}
            </Box>

            <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
              <ChartCard title="Submissions by Source" height={260}>
                <PieChart>
                  <Pie
                    data={sourceData}
                    dataKey="value"
                    nameKey="name"
                    innerRadius={55}
                    outerRadius={90}
                    paddingAngle={2}
                    label={(entry: any) => entry.value}
                    isAnimationActive={false}
                  >
                    {sourceData.map((entry: any) => (
                      <Cell
                        key={entry.key}
                        fill={sourceColors[entry.key] || "#9e9e9e"}
                      />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ChartCard>

              <ChartCard title="Submissions by Month" height={260}>
                <BarChart
                  data={monthlyData}
                  margin={{ top: 20, bottom: 10, right: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Legend />
                  <Bar
                    dataKey="Assigned"
                    stackId="a"
                    fill="#2e7d32"
                    isAnimationActive={false}
                    radius={[0, 0, 0, 0]}
                  />
                  <Bar
                    dataKey="Open"
                    stackId="a"
                    fill="#fdb814"
                    isAnimationActive={false}
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ChartCard>
            </Box>

            <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
              <ChartCard title="Top Cities" height={280}>
                <BarChart
                  data={cityChartData}
                  margin={{ top: 20, bottom: 70, right: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis
                    dataKey="name"
                    interval={0}
                    angle={-30}
                    tick={{ fontSize: 10, textAnchor: "end" }}
                  />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Bar
                    dataKey="Submissions"
                    fill={PRIMARY}
                    isAnimationActive={false}
                    radius={[4, 4, 0, 0]}
                  >
                    <LabelList dataKey="Submissions" position="top" />
                  </Bar>
                </BarChart>
              </ChartCard>

              <ChartCard
                title="Ownership Demographics"
                subtitle="Self-reported on the intake form"
                height={280}
              >
                <BarChart
                  data={ownershipData}
                  margin={{ top: 20, bottom: 10, right: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Bar
                    dataKey="Businesses"
                    fill="#3F51B5"
                    isAnimationActive={false}
                    radius={[4, 4, 0, 0]}
                  >
                    <LabelList dataKey="Businesses" position="top" />
                  </Bar>
                </BarChart>
              </ChartCard>
            </Box>

            <SectionCard>
              <SectionTitle subtitle="Expand a source to see its submissions">
                By Source
              </SectionTitle>
              {/* Scrolls sideways on its own when wider than the card. */}
              <Box sx={{ overflowX: "auto" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: 40 }} />
                      <TableCell>Source</TableCell>
                      <TableCell align="right">Submissions</TableCell>
                      <TableCell align="right">Open</TableCell>
                      <TableCell align="right">Assigned</TableCell>
                      <TableCell align="right">Consulted</TableCell>
                      <TableCell align="right">Assignment Rate</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {report.bySource.map((source: any) => (
                      <SourceRow
                        key={source.source}
                        source={source}
                        submissionRows={rowsBySource[source.source] || []}
                      />
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </SectionCard>

            <SectionCard>
              <SectionTitle subtitle="How submitters heard about Oakland Thrive">
                Attribution
              </SectionTitle>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>How They Heard</TableCell>
                    <TableCell align="right">Submissions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {report.byHeardAbout.map((entry: any) => (
                    <TableRow key={entry.source}>
                      <TableCell>{entry.source}</TableCell>
                      <TableCell align="right">{entry.total}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </SectionCard>
          </Stack>
        )}
      </Box>
    </Box>
  );
};

export default WebIntakeReport;
